import { IFieldProps } from "types/Organisational";

const CUSTOM_FIELD_IDS = new Set([
  "cost_center",
  "custom_field_01",
  "custom_field_02",
  "custom_field_03",
  "reason_code",
  "reason_code_group",
  "custom_long_field_01",
]);

const LOCKED_FIELD_IDS = new Set(["location", "invoice_location"]);

const GRID_EXCLUDED_FIELD_IDS = new Set(["shipment_title", "invoice_location", "location", "cost_recovery_required"]);

const toSelectOptions = (values: Array<{ key: string; value: string }>) =>
  values.map(({ key, value }) => ({ label: key, value }));

export function buildOrganisationFields(rawOptions: IFieldProps[] | undefined, order: string[] = []): IFieldProps[] {
  if (!rawOptions?.length) return [];

  const fields = rawOptions
    .filter((item) => item.field_id && !LOCKED_FIELD_IDS.has(item.field_id))
    /** The modal shows every free-text field as a single-line input, so long fields lose their textarea type. */
    .map((item) => (item.type === "textarea" ? { ...item, type: "textinput" } : item))
    .map((item) => {
      if (!item.dropdown_values?.length) return item;
      return {
        ...item,
        dropdown_values: [...item.dropdown_values]
          .sort((a, b) => a.key?.localeCompare(b.key))
          .map((val) => ({ ...val, ...toSelectOptions([val])[0] })),
      };
    });

  const customFields = fields.filter((item) => CUSTOM_FIELD_IDS.has(item.field_id));
  const otherFields = fields.filter((item) => !CUSTOM_FIELD_IDS.has(item.field_id));
  const orderedCustomFields = [...customFields].sort((a, b) => order.indexOf(a.field_id) - order.indexOf(b.field_id));

  const reasonCodeGroupIds = orderedCustomFields.findIndex((f) => f.field_id === "reason_code_group");
  const reasonCodeIds = orderedCustomFields.findIndex((f) => f.field_id === "reason_code");
  if (reasonCodeGroupIds !== -1 && reasonCodeIds !== -1 && reasonCodeGroupIds > reasonCodeIds) {
    const [rcg] = orderedCustomFields.splice(reasonCodeGroupIds, 1);
    orderedCustomFields.splice(reasonCodeIds, 0, rcg);
  }

  return [...otherFields, ...orderedCustomFields];
}

export function isGridOrganisationField(fieldId: string): boolean {
  return !GRID_EXCLUDED_FIELD_IDS.has(fieldId) && !fieldId.startsWith("custom_cost_recovery_");
}

export function getOrganisationDropdownOptions(
  field: IFieldProps,
  organisationFields: IFieldProps[],
  reasonCodeGroup?: string | null,
): Array<{ label: string; value: string }> {
  const { field_id, dropdown_values } = field;
  if (!dropdown_values?.length) return [];

  if (field_id === "reason_code") {
    const hasReasonCodeGroup = organisationFields.some(
      (item) => item.field_id === "reason_code_group" && item.dropdown_values?.length,
    );
    if (hasReasonCodeGroup) {
      if (!reasonCodeGroup) return [];
      return dropdown_values
        .filter((item) => (item as { group_name?: string }).group_name === reasonCodeGroup)
        .map((item) => ({ label: item.key, value: item.value }));
    }
  }

  return dropdown_values.map((item) => ({
    label: field_id === "cost_center" ? item.key.replaceAll("\n", "") : item.key,
    value: item.value,
  }));
}

export function getCostRecoveryYesValue(organisationFields: IFieldProps[]): string {
  const costRecoveryField = organisationFields.find((f) => f.field_id === "cost_recovery_required");
  return (
    (costRecoveryField?.dropdown_values as Array<{ key: string; value: string }> | undefined)?.find(
      (item) => item.key === "Yes",
    )?.value ?? "True"
  );
}
